import { rem } from 'polished'
import React, { useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatPrice } from 'shared-constants'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { Button, BUTTON_INTENT, BUTTON_VARIANT } from 'components/Button'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import { Box, Flex } from 'components/Layout'
import { Loader, LOADER_SIZE } from 'components/Loader'
import NoData from 'components/NoData'
import { Select } from 'components/Select'
import { H4, Text } from 'components/Typography'
import {
  DEFAULT_CURRENCY_ISO_CODE,
  REVENUE_PERIOD_STATES,
} from 'constants/common'
import { useUserAuth } from 'providers/userAuth'
import { COLOR, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import { useMedia } from 'useMedia'
import {
  BAR_CHART_CUP_RADIUS,
  BAR_SIZE,
  BASE_RADIUS,
  PERIOD_DATE_GROUP_STATES,
  X_AXIS_DIAGONAL_PROPS,
} from '../chartHelpers'
import { useExpertRevenuesQuery } from './hooks'
import { RevenueSummaryLegend } from './RevenueSummaryLegend'
import { prepareDataForCharts, renderTooltip } from './utils'

export const WIDGET_AREA_HEIGHT = rem(400)
export const CHART_MIN_HEIGHT = rem(250)

const StyledText = styled(Text)`
  white-space: nowrap;
`

const HorizontalBarContainer = styled(Flex)`
  border-radius: ${BASE_RADIUS}px;
  overflow: hidden;
  background-clip: border-box;
`

const HorizontalBar = styled('div')`
  flex: 0 0 ${({ total, value }) => `${value ? (value / total) * 100 : 0}%`};
  background-color: ${({ color }) => color};
`

export const RevenueChartWidget = () => {
  const media = useMedia()
  const { user, convertDateToTimezone } = useUserAuth()
  const [currentCurrency, setCurrentCurrency] = useState(
    user.expert?.currencyIsoCode ?? DEFAULT_CURRENCY_ISO_CODE
  )
  const [periodDateGroup, setPeriodDateGroup] = useState(
    REVENUE_PERIOD_STATES.THIS_MONTH
  )
  const period = PERIOD_DATE_GROUP_STATES[periodDateGroup]({
    expertCreated: convertDateToTimezone(
      new Date(user.expert?.createdAtUtc.isoFormat)
    ),
    now: convertDateToTimezone(new Date()),
  })
  const { isLoading, data: expertRevenuesData } = useExpertRevenuesQuery({
    expertId: user.id,
    period,
  })

  const { currency, currenciesList, data, total, yAxisFormatter } =
    prepareDataForCharts({
      sessionRevenues: expertRevenuesData,
      periodDateGroup,
      currencyOverride: currentCurrency,
      convertDateToTimezone,
    })

  return (
    <Card
      flexDirection="column"
      width="100%"
      elevation={CARD_ELEVATION.NONE}
      paddingSize={CARD_PADDING_SIZE.NONE}
      data-test-id={DATA_TEST_ID.REVENUE_CHART_WIDGET}
      hasBorders
    >
      <Flex flexDirection="column" p={SPACE.PX_30}>
        <Flex flexDirection="row" flexWrap="wrap" pb={SPACE.PX_15}>
          <Flex flex="1" alignItems="center" flexWrap="nowrap">
            <StyledText
              fontSize={{ MOBILE: FONT_SIZE.PX_16, TABLET: FONT_SIZE.PX_20 }}
              fontWeight={FONT_WEIGHT.MEDIUM}
              color={COLOR.GRAYSCALE_1}
            >
              Revenue
            </StyledText>
            {!isLoading && (
              <Flex ml={SPACE.PX_10} flex={`0 1 ${SPACE.PX_80}`}>
                {currenciesList.length > 1 && (
                  <Select
                    name="Currency"
                    options={currenciesList.map((currencyIsoCode) => ({
                      label: currencyIsoCode,
                      value: currencyIsoCode,
                    }))}
                    value={currentCurrency}
                    onChange={({ value }) => {
                      setCurrentCurrency(value)
                    }}
                  />
                )}
              </Flex>
            )}
          </Flex>
          <Flex
            selfAlign="end"
            flexWrap="wrap"
            maxWidth="fit-content"
            mr={`-${SPACE.PX_15}`}
          >
            <Button
              width="auto"
              variant={BUTTON_VARIANT.MINIMAL}
              intent={
                periodDateGroup === REVENUE_PERIOD_STATES.ALL_TIME
                  ? BUTTON_INTENT.PRIMARY
                  : BUTTON_INTENT.NONE
              }
              onClick={() => {
                setPeriodDateGroup(REVENUE_PERIOD_STATES.ALL_TIME)
              }}
            >
              All time
            </Button>
            <Button
              width="auto"
              variant={BUTTON_VARIANT.MINIMAL}
              intent={
                periodDateGroup === REVENUE_PERIOD_STATES.LAST_MONTH
                  ? BUTTON_INTENT.PRIMARY
                  : BUTTON_INTENT.NONE
              }
              onClick={() => {
                setPeriodDateGroup(REVENUE_PERIOD_STATES.LAST_MONTH)
              }}
            >
              Last month
            </Button>
            <Button
              width="auto"
              variant={BUTTON_VARIANT.MINIMAL}
              intent={
                periodDateGroup === REVENUE_PERIOD_STATES.THIS_MONTH
                  ? BUTTON_INTENT.PRIMARY
                  : BUTTON_INTENT.NONE
              }
              onClick={() => {
                setPeriodDateGroup(REVENUE_PERIOD_STATES.THIS_MONTH)
              }}
            >
              This month
            </Button>
          </Flex>
        </Flex>
        {data?.chartData?.length ? (
          <>
            <H4 color={COLOR.PRIMARY_1} pb={SPACE.PX_20}>
              {formatPrice({
                price: total,
                currency,
              })}
            </H4>
            <Box>
              <ResponsiveContainer width="100%" minHeight={CHART_MIN_HEIGHT}>
                <BarChart data={data.chartData} margin={0}>
                  <CartesianGrid strokeDasharray="3" vertical={false} />
                  <XAxis
                    dataKey="columnLabel"
                    fontSize={FONT_SIZE.PX_12}
                    {...X_AXIS_DIAGONAL_PROPS}
                  />
                  <YAxis
                    fontSize={FONT_SIZE.PX_14}
                    width={media.MOBILE ? BAR_SIZE.M : BAR_SIZE.L}
                    label={{ scaleToFit: true }}
                    tickFormatter={yAxisFormatter}
                  />
                  <Tooltip
                    cursor={{ opacity: 0.2 }}
                    contentStyle={{ fontSize: FONT_SIZE.PX_14 }}
                    labelStyle={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                    separator=": "
                    formatter={(value, name) =>
                      renderTooltip(value, name, currency)
                    }
                  />
                  <Bar
                    dataKey="paid"
                    stackId="revenue"
                    isAnimationActive={
                      process.env.NEXT_PUBLIC_NODE_ENV !== 'test'
                    }
                    barSize={media.MOBILE ? BAR_SIZE.S : BAR_SIZE.M}
                    fill={COLOR.PRIMARY_1}
                    radius={BAR_CHART_CUP_RADIUS}
                    shape={(bar) => (
                      <Rectangle
                        {...bar}
                        radius={bar.pending > 0 ? 0 : bar.radius}
                      />
                    )}
                  />
                  <Bar
                    dataKey="pending"
                    stackId="revenue"
                    isAnimationActive={
                      process.env.NEXT_PUBLIC_NODE_ENV !== 'test'
                    }
                    fill={COLOR.SECONDARY_8}
                    radius={BAR_CHART_CUP_RADIUS}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Flex width="100%" pb={SPACE.PX_15}>
              <RevenueSummaryLegend
                width="50%"
                label="Completed"
                color={COLOR.PRIMARY_1}
                price={data.paidTotal}
                currency={currency}
              />
              <RevenueSummaryLegend
                width="50%"
                label="Pending"
                color={COLOR.SECONDARY_8}
                price={data.pendingTotal}
                currency={currency}
              />
            </Flex>
            <HorizontalBarContainer width="100%" height={SPACE.PX_30}>
              <HorizontalBar
                total={total}
                value={data.paidTotal}
                color={COLOR.PRIMARY_1}
              />
              <HorizontalBar
                total={total}
                value={data.pendingTotal}
                color={COLOR.SECONDARY_8}
              />
            </HorizontalBarContainer>
          </>
        ) : (
          <Flex
            alignItems="center"
            justifyContent="center"
            height={WIDGET_AREA_HEIGHT}
          >
            {isLoading ? (
              <Loader size={LOADER_SIZE.S} />
            ) : (
              <NoData
                header="No revenues in selected range"
                content="There will be displayed your revenues"
              />
            )}
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
